import { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useLoaderData } from "react-router";

import { BadgeIcon } from "@components/Badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@components/DropdownMenu";
import { Image } from "@components/Image";
import { Typography } from "@components/Typography";

import { useNewsListQuery } from "@storage/queries/news";
import { TSessionData } from "@storage/session/types";

import { QUERY_KEYS } from "@configs/api";
import { DELAY_TIME_3000 } from "@configs/constants";
import { ENV_CONFIG } from "@configs/env";
import { ICON_PATHS } from "@configs/icons";

export const NewsListContainer = () => {
	const [isOpen, setIsOpen] = useState(false);

	const { currentAccount } = useLoaderData<TSessionData>();

	const newsListQuery = useNewsListQuery();

	const newsLength = newsListQuery.isSuccess ? newsListQuery.data.length : 0;
	const hasNews = newsLength > 0;
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!hasNews) {
			setIsOpen(false);
		}
	}, [hasNews]);

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open && hasNews);
	};

	const handleClickNews = (newsId: string) => {
		setTimeout(() => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NEWS_DATA] });
		}, DELAY_TIME_3000);
		window.open(`${ENV_CONFIG.BASE_HUB_URL}news/${newsId}?modelName=${currentAccount.bss_username}`, "_blank");
	};

	return (
		<DropdownMenu disabled={!hasNews} open={hasNews ? isOpen : false} onOpenChange={handleOpenChange}>
			<DropdownMenuTrigger openOnHover className="hover:rounded-lg hover:bg-grey-hover">
				<div className="relative size-10">
					<Image width={40} height={40} src={ICON_PATHS.BELL} className={!hasNews ? "opacity-50" : ""} alt="bell" />
					{hasNews ? (
						<BadgeIcon variant="error" className="absolute top-0 right-0 h-5 w-5 text-xs" symbol={newsLength} />
					) : null}
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className={`custom-scrollbar max-h-112 w-[24rem] overflow-y-auto rounded-lg p-0
					shadow-[0_2px_20px_0_rgba(0,0,0,0.90)]`}
			>
				{newsListQuery.isSuccess
					? newsListQuery.data.map((item) => (
							<DropdownMenuItem
								key={item.id}
								onClick={() => handleClickNews(item.link)}
								className="flex cursor-pointer items-center gap-4 rounded-lg p-3 hover:bg-ground-hover"
							>
								<Image
									className="max-h-14 min-h-14 max-w-18.5 min-w-18.5 rounded-sm"
									alt="news_image"
									src={item.image || ICON_PATHS.NEWS_DEFAULT}
								/>
								<Typography weight="bold" padding="none" className="max-h-12.5 overflow-hidden text-ellipsis">
									{item.title}{" "}
									<Typography type="span" color="msg_time" padding="none">
										{item.description}
									</Typography>
								</Typography>

								<ChevronRight className="h-1.75 w-3" color="#8e8793" strokeWidth={1.5} absoluteStrokeWidth />
							</DropdownMenuItem>
						))
					: null}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
